import type { GetOneDiscount_content_discount } from '@/queries/types/GetOneDiscount';
import {
  getSupportedDiscountGenerations,
  RingGenerations,
} from '@/utils/discountUtils';

import {
  ERROR_EXPIRED,
  ERROR_NO_USES_REMAIN,
  ERROR_NOT_FOUND,
} from '../_consts/discounts';
import type { DiscountContextValues } from '../_contexts/DiscountProvider';

interface ShowDiscountMessage
  extends Pick<
    DiscountContextValues,
    'urlDiscount' | 'urlDiscountIsSuccess' | 'urlPromotionData'
  > {
  isAccessory: boolean;
  isOuraRing4Wizard: boolean;
}

interface GetValidDiscountStatus {
  hasValidGen3DiscountForGen3Wiz: boolean | null | undefined;
  hasValidGen4DiscountForGen4Wiz: boolean | null | undefined;
  hasValidAllRingsDiscount: boolean | null | undefined;
}

// _getIsNotGen4Discount takes a Discount and returns true if no gen4 ring is represented on it.
const _getIsNotGen4Discount = (
  discount: GetOneDiscount_content_discount | null,
): boolean => {
  const supportedDiscountGenerations =
    getSupportedDiscountGenerations(discount);
  return !supportedDiscountGenerations.has('gen4');
};

// _getIsAllRingsDiscount takes a Discount and returns true if both gen4 and any previous generation rings are represented on it.
const _getIsAllRingsDiscount = (
  discount: GetOneDiscount_content_discount | null,
): boolean => {
  const supportedDiscountGenerations =
    getSupportedDiscountGenerations(discount);

  // The set will contain at most the number of items in list of generations.
  return supportedDiscountGenerations.size === RingGenerations.length;
};

const _getValidDiscountStatus = ({
  isOuraRing4Wizard,
  urlDiscount,
  urlDiscountIsSuccess,
}: Omit<
  ShowDiscountMessage,
  'isAccessory' | 'urlPromotionData'
>): GetValidDiscountStatus => {
  const validDiscountStatus: GetValidDiscountStatus = {
    hasValidGen3DiscountForGen3Wiz: null,
    hasValidGen4DiscountForGen4Wiz: null,
    hasValidAllRingsDiscount: null,
  };
  if (!urlDiscount) return validDiscountStatus;
  const isNotGen4Discount = _getIsNotGen4Discount(urlDiscount);
  const isAllRingsDiscount = _getIsAllRingsDiscount(urlDiscount);
  const isSuccessWithPayload = urlDiscountIsSuccess && urlDiscount;

  // If the discount is valid for Gen3 wizard, show the discount message on Gen3 only
  validDiscountStatus.hasValidGen3DiscountForGen3Wiz =
    isSuccessWithPayload && !isOuraRing4Wizard && isNotGen4Discount;
  // If the discount is valid for Gen4 wizard, show the discount message on Gen4 only
  validDiscountStatus.hasValidGen4DiscountForGen4Wiz =
    isSuccessWithPayload && isOuraRing4Wizard && !isNotGen4Discount;
  // If the discount is valid for all rings, show the discount message on all rings
  validDiscountStatus.hasValidAllRingsDiscount =
    isSuccessWithPayload && isAllRingsDiscount;

  return validDiscountStatus;
};

const showDiscountMessage = ({
  isAccessory,
  isOuraRing4Wizard,
  urlDiscount,
  urlDiscountIsSuccess,
  urlPromotionData,
}: ShowDiscountMessage): boolean => {
  const {
    hasValidGen3DiscountForGen3Wiz,
    hasValidGen4DiscountForGen4Wiz,
    hasValidAllRingsDiscount,
  } = _getValidDiscountStatus({
    isOuraRing4Wizard,
    urlDiscount,
    urlDiscountIsSuccess,
  });
  // If no discount is found, show the discount message
  const noDiscountFound = urlPromotionData?.invalidReason === ERROR_NOT_FOUND;
  // If the discount is expired, show the discount message
  const hasExpiredDiscount = urlPromotionData?.invalidReason === ERROR_EXPIRED;
  // If the discount usage limit is reached, show the discount message
  const hasLimitReached =
    urlPromotionData?.invalidReason === ERROR_NO_USES_REMAIN;

  switch (true) {
    case isAccessory:
      return false;
    case hasValidGen3DiscountForGen3Wiz:
      return true;
    case hasValidGen4DiscountForGen4Wiz:
      return true;
    case hasValidAllRingsDiscount:
      return true;
    case noDiscountFound:
      return true;
    case hasExpiredDiscount:
      return true;
    case hasLimitReached:
      return true;
    default:
      return false;
  }
};

export default showDiscountMessage;
